import { defineStore } from 'pinia'

import apiClient from '@/services/http/client'
import type { AppContext, AppContextSubscription, OnboardingInfo } from '@/types'

interface AppContextState {
  subscription: AppContextSubscription | null
  onboarding: OnboardingInfo | null
  loaded: boolean
  loading: boolean
  loadError: boolean
}

export const useAppContextStore = defineStore('app-context', {
  state: (): AppContextState => ({
    subscription: null,
    onboarding:   null,
    loaded:       false,
    loading:      false,
    loadError:    false,
  }),

  getters: {
    // ── Onboarding ─────────────────────────────────────────────────────────

    /** El onboarding está completo cuando el backend así lo indica */
    onboardingComplete: (state): boolean => state.onboarding?.completed ?? false,

    /** Próximo paso pendiente según el backend; null = todos completos */
    nextStep: (state): string | null => state.onboarding?.next_step ?? null,

    /**
     * URL de destino post-onboarding según el backend.
     * Normalizada con slash inicial para evitar rutas relativas en Vue Router.
     */
    redirectTo: (state): string | null => {
      const raw = state.onboarding?.redirect_to ?? null
      if (!raw) return null
      return raw.startsWith('/') ? raw : `/${raw}`
    },

    // ── Suscripción ────────────────────────────────────────────────────────

    /** Fuente de verdad para habilitar/bloquear módulos que requieren suscripción */
    canUseWhatsapp: (state): boolean =>
      state.subscription?.can_use_whatsapp ?? false,

    /** True si la suscripción está activa o en período de prueba */
    hasActiveSubscription: (state): boolean => {
      const s = state.subscription?.status
      return s === 'active' || s === 'trialing'
    },

    // ── Compat: AppLayout usa estos getters para el badge de prueba ────────

    /** @deprecated Derivado de subscription.status */
    isTrial: (state): boolean =>
      state.subscription?.status === 'trialing',

    /** @deprecated Calculado a partir de subscription.trial_ends_at */
    trialDaysRemaining: (state): number | null => {
      const endsAt = state.subscription?.trial_ends_at
      if (!endsAt) return null
      const diff = new Date(endsAt).getTime() - Date.now()
      return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)))
    },
  },

  actions: {
    async loadContext() {
      if (this.loaded || this.loading) return
      this.loading   = true
      this.loadError = false
      try {
        const response = await apiClient.get<{ data: AppContext }>('/api/v1/app/context')
        this.subscription = response.data.data.subscription ?? null
        this.onboarding   = response.data.data.onboarding
        this.loaded = true
      } catch {
        // El contexto es crítico para la navegación.
        // No asumir onboarding completo — marcar el error para que el guard lo maneje.
        this.loadError = true
        this.loaded    = true
      } finally {
        this.loading = false
      }
    },

    markStepComplete(stepKey: string) {
      if (!this.onboarding) return
      if (!this.onboarding.steps || typeof this.onboarding.steps !== 'object') {
        this.onboarding.steps = {}
      }
      this.onboarding.steps[stepKey] = true
    },

    markOnboardingComplete() {
      if (this.onboarding) {
        this.onboarding.completed    = true
        this.onboarding.all_steps_done = true
        this.onboarding.next_step    = null
        this.onboarding.redirect_to  = '/'
      }
    },

    reset() {
      this.subscription = null
      this.onboarding   = null
      this.loaded       = false
      this.loading      = false
      this.loadError    = false
    },

    // Super admin no tiene contexto de studio — marcamos como loaded sin onboarding.
    markSuperAdmin() {
      this.loaded    = true
      this.loadError = false
      this.onboarding = {
        completed:      true,
        completed_at:   null,
        all_steps_done: true,
        next_step:      null,
        redirect_to:    '/admin',
        steps:          {},
      }
    },

    /**
     * Recarga forzada que resetea `loaded = false` antes de fetchear.
     * Úsalo solo cuando quieres bloquear la UI mientras carga (ej: botón de retry).
     * NO usar en onMounted ni en flujos silenciosos — causa desmontaje de vistas.
     */
    async reload() {
      this.loaded    = false
      this.loadError = false
      await this.loadContext()
    },

    /**
     * Refresca el contexto en background SIN resetear `loaded`.
     * No afecta `appReady` en App.vue, por lo que el componente no se desmonta.
     * Úsalo para actualizaciones post-Stripe y post-step dentro del onboarding.
     */
    async refresh() {
      if (this.loading) return
      this.loading   = true
      this.loadError = false
      try {
        const response = await apiClient.get<{ data: AppContext }>('/api/v1/app/context')
        this.subscription = response.data.data.subscription ?? null
        this.onboarding   = response.data.data.onboarding
      } catch {
        this.loadError = true
      } finally {
        this.loading = false
      }
    },
  },
})
